const AUDIO_UPLOAD_TIMEOUT_MS = 30000;

const withUploadTimeout = (promise) => new Promise((resolve, reject) => {
  const timeoutId = setTimeout(
    () => reject(new Error('Audio upload timed out. Please try again.')),
    AUDIO_UPLOAD_TIMEOUT_MS
  );
  promise.then(
    (value) => {
      clearTimeout(timeoutId);
      resolve(value);
    },
    (error) => {
      clearTimeout(timeoutId);
      reject(error);
    }
  );
});

export async function generateAudio(text, voice, token) {
  if (!text) {
    throw new Error('No text provided');
  }

  if (!voice) {
    console.warn('No voice provided for TTS, using default.');
  }

  const response = await fetch('/api/generate-audio', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ text, voice }),
  });

  const contentType = response.headers.get('content-type') || '';
  if (!response.ok) {
    const errorMessage = contentType.includes('application/json') ?
      (await response.json()).error :
      await response.text();
    throw new Error(errorMessage || 'TTS API call failed.');
  }

  const arrayBuffer = await response.arrayBuffer();
  return arrayBuffer;
}

/**
 * Upload a generated audio buffer to Firebase Storage and return a download URL.
 * Keyed by storyId + voice so multiple voices can coexist on the same story.
 */
export async function uploadAudioToStorage(audioBuffer, storyId, voice, userId, pageNumber = null, token = null) {
  if (!storyId || !voice || !userId) {
    throw new Error('userId, storyId, and voice are required to persist audio');
  }
  const bytes = new Uint8Array(audioBuffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  const dataUrl = `data:audio/wav;base64,${btoa(binary)}`;
  const pageKey = pageNumber === null || pageNumber === undefined ? null : String(pageNumber);
  const fileName = pageKey ? `${voice}/page-${pageKey}.wav` : `${voice}.wav`;
  const response = await withUploadTimeout(fetch('/api/upload-storage-asset', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ storyId, fileName, dataUrl, assetType: 'audio' }),
  }));
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.downloadUrl) throw new Error(result.error || 'Could not store narration audio.');
  return result.downloadUrl;
}
